import { useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { cartAtom } from './cart.store'
import type { V2CartItem } from './cart.store'
import { fgProductsAtom, fgProductsV2Atom, fgSelectedItemVersionAtom } from '@/core/feelgreat/feelgreat.store'
import { getProductPrice, type PriceMode } from './useCart'
import { localize } from '@/core/utils'
import type { FGProduct, FGProductV2, FGProductV2Variant, FGProductV2OptionItem } from '@/core/feelgreat/feelgreat.types'

export interface V1CartEntry {
    purchaseType: string
    flavor: string
    product: FGProduct | undefined
    qty: number
    unitPrice: number
}

export interface V2CartEntry {
    name: string
    flavor?: string
    qty: number
    unitPrice: number
    imageUrl?: string
    isSubscribe: boolean
}

const findVariant = (product: FGProductV2, selection: Record<string, string>) => {
    const matches = product.variants
        .filter(v => Object.keys(v.combination).every(k => selection[k] === v.combination[k]))
        .sort((a, b) => Object.keys(b.combination).length - Object.keys(a.combination).length)
    return matches[0] ?? null
}

const findOptionItem = (variant: FGProductV2Variant, purchaseOption: string) => variant.optionItems.find(item => item.option === purchaseOption) ?? null

const getOptionItemPrice = (item: FGProductV2OptionItem, mode: PriceMode) => (mode === 'member' ? (item.price.member ?? item.price.retail) : item.price.retail)

export const useV1CartEntries = (priceMode: PriceMode): V1CartEntry[] => {
    const cart = useAtomValue(cartAtom)
    const fgProducts = useAtomValue(fgProductsAtom)

    return useMemo(() => {
        const entries: V1CartEntry[] = []
        for (const [purchaseType, flavorMap] of Object.entries(cart)) {
            for (const [flavor, qty] of Object.entries(flavorMap)) {
                if (qty > 0) {
                    entries.push({
                        purchaseType,
                        flavor,
                        product: fgProducts[flavor]?.[purchaseType],
                        qty,
                        unitPrice: getProductPrice(fgProducts, flavor, purchaseType, priceMode)
                    })
                }
            }
        }
        return entries
    }, [cart, fgProducts, priceMode])
}

export const useV2CartEntries = (v2Cart: V2CartItem[], priceMode: PriceMode): V2CartEntry[] => {
    const productsV2 = useAtomValue(fgProductsV2Atom)

    return useMemo(() => {
        return v2Cart.flatMap(item => {
            const product = productsV2.find(p => p.product_id === item.product_id)
            if (!product) return []
            const variant = findVariant(product, item.selection)
            if (!variant) return []
            const optionItem = findOptionItem(variant, item.purchase_option)
            if (!optionItem) return []
            return [
                {
                    name: localize(optionItem.item_name) || localize(product.name),
                    flavor: product.options[0]?.values.find(v => v.id === item.selection.option_index_0)?.name.english,
                    qty: item.qty,
                    unitPrice: getOptionItemPrice(optionItem, priceMode),
                    imageUrl: optionItem.image_url ?? product.packImages,
                    isSubscribe: item.purchase_option === 'subscribe'
                }
            ]
        })
    }, [v2Cart, productsV2, priceMode])
}

/**
 * Local FE-computed pricing that uses cart content only (no server data).
 *
 * Two flavours per field exist in the checkout flow:
 *   - **Local** (this hook)            — instant, derived from `cartAtom`
 *     and `fgProducts`. Always available. Used by countries that don't
 *     round-trip to `hot/format` (e.g. Thailand) and as the fallback for
 *     countries that do (Oman, Kuwait, Singapore) before the format
 *     response arrives.
 *   - **Server** (per-country pricing) — pulled from `*FormatDataAtom`
 *     after `GET hot/format`. Includes shipping fee, product credit, and
 *     adjustments the backend applied (freebies, promotions, tax). Each
 *     country composes the two via its own `useXxxOrderPricing`.
 */
export const useCartLocalPricing = (priceMode: PriceMode, v2CartItems: V2CartItem[] = []) => {
    const v1Entries = useV1CartEntries(priceMode)
    const v2CartEntries = useV2CartEntries(v2CartItems, priceMode)
    const version = useAtomValue(fgSelectedItemVersionAtom)

    const isV2 = version === 'v2'
    const hasV1Cart = !isV2 && v1Entries.length > 0
    const hasV2Cart = isV2 && v2CartEntries.length > 0

    const subtotal = useMemo(() => {
        if (hasV2Cart) return v2CartEntries.reduce((sum, e) => sum + e.unitPrice * e.qty, 0)
        if (hasV1Cart) return v1Entries.reduce((sum, e) => sum + e.unitPrice * e.qty, 0)
        return 0
    }, [hasV1Cart, hasV2Cart, v1Entries, v2CartEntries])

    return { v1Entries, v2CartEntries, isV2, hasV1Cart, hasV2Cart, subtotal }
}
